const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  variantChange: "variant-change",
  cartError: "cart-error",
};

/*
 * APOLLON — quantity rules shared by product pages, cart drawer and cart page.
 *
 * Rules are based on Shopify product type so they keep working across variants,
 * languages and product titles.
 */
(function () {
  if (window.apollonQuantityRulesLoaded) return;
  window.apollonQuantityRulesLoaded = true;

  const QUANTITY_RULES = {
    "Stretched Canvas": { min: 2, step: 2 },
    "Stretched canvas": { min: 2, step: 2 },
    Moulding: { min: 100, step: 5 },
  };

  const knownVariants = new Map();
  const correctedCartLines = new Set();
  let refreshInProgress = false;
  let applyQueued = false;
  let currentProductRule = null;

  function normalizeQuantity(value, rule, allowZero) {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed)) return rule.min;
    if (allowZero && parsed <= 0) return 0;
    if (parsed <= rule.min) return rule.min;

    const distance = parsed - rule.min;
    const remainder = distance % rule.step;
    return remainder === 0 ? parsed : parsed + (rule.step - remainder);
  }

  function updateButtonStates(input) {
    const quantityInput = input.closest("quantity-input");
    if (quantityInput && typeof quantityInput.updateButtonStates === "function") {
      quantityInput.updateButtonStates();
    }
  }

  function setInputRule(input, rule) {
    input.setAttribute("min", String(rule.min));
    input.setAttribute("data-min", String(rule.min));
    input.setAttribute("step", String(rule.step));
    input.dataset.apollonQuantityRule = "true";
    updateButtonStates(input);
  }

  /* ---------- PRODUCT PAGE ---------- */

  function configureProductInput(input) {
    if (!currentProductRule) return;

    setInputRule(input, currentProductRule);

    if (input === document.activeElement) return;

    const current = parseInt(input.value, 10);
    const next = normalizeQuantity(current, currentProductRule, false);
    if (Number.isNaN(current) || next !== current) {
      input.value = String(next);
    }
    updateButtonStates(input);
  }

  function applyProductRule() {
    if (!currentProductRule) return;
    document.querySelectorAll('input[name="quantity"]').forEach(configureProductInput);
  }

  function loadProductRule() {
    if (!window.location.pathname.includes("/products/")) return;

    const productUrl = `${window.location.pathname.replace(/\.js$/, "")}.js`;
    fetch(productUrl, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        return response.json();
      })
      .then((product) => {
        currentProductRule = QUANTITY_RULES[product.type] || null;
        applyProductRule();
      })
      .catch((error) => {
        console.error("Unable to apply Apollon product quantity rule:", error);
      });
  }

  function normalizeProductInput(input) {
    if (!currentProductRule || input.name !== "quantity") return;
    const current = parseInt(input.value, 10);
    const next = normalizeQuantity(current, currentProductRule, false);
    if (Number.isNaN(current) || next !== current) {
      input.value = String(next);
    }
    setInputRule(input, currentProductRule);
    updateButtonStates(input);
  }

  document.addEventListener(
    "change",
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.name !== "quantity") return;
      normalizeProductInput(input);
    },
    true
  );

  document.addEventListener(
    "focusout",
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.name !== "quantity") return;
      normalizeProductInput(input);
    },
    true
  );

  document.addEventListener(
    "submit",
    (event) => {
      if (!currentProductRule) return;
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      form.querySelectorAll('input[name="quantity"]').forEach(normalizeProductInput);
    },
    true
  );

  /* ---------- CART DRAWER + CART PAGE ---------- */

  function cartLineKey(input, item) {
    const surface = input.closest("cart-drawer-items") ? "drawer" : "page";
    return `${surface}:${item.dataset.variantId || ""}:${input.dataset.index || input.id || ""}`;
  }

  function configureCartInput(input, item, rule) {
    setInputRule(input, rule);

    if (input === document.activeElement) return;

    const current = parseInt(input.value, 10);
    const next = normalizeQuantity(current, rule, true);
    if (Number.isNaN(current) || current <= 0 || next === current) return;

    const key = cartLineKey(input, item);
    if (correctedCartLines.has(key)) return;
    correctedCartLines.add(key);

    input.value = String(next);
    updateButtonStates(input);
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function applyCartRules() {
    let hasUnknownVariant = false;

    document.querySelectorAll(".cart-item[data-variant-id]").forEach((item) => {
      const variantId = String(item.dataset.variantId || "");
      const input = item.querySelector('input[name^="updates["]');
      if (!variantId || !input) return;

      if (!knownVariants.has(variantId)) {
        hasUnknownVariant = true;
        return;
      }

      const productType = knownVariants.get(variantId);
      const rule = QUANTITY_RULES[productType];
      if (rule) configureCartInput(input, item, rule);
    });

    if (hasUnknownVariant) refreshCartTypes();
  }

  function hydrateFromCart(cart) {
    knownVariants.clear();

    if (cart && Array.isArray(cart.items)) {
      cart.items.forEach((item) => {
        knownVariants.set(String(item.variant_id), item.product_type || "");
      });
    }

    applyCartRules();
  }

  function refreshCartTypes() {
    if (refreshInProgress) return;
    if (!window.routes || !routes.cart_url) return;

    refreshInProgress = true;
    fetch(`${routes.cart_url}.js`, { headers: { Accept: "application/json" } })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        return response.json();
      })
      .then(hydrateFromCart)
      .catch((error) => {
        console.error("Unable to apply Apollon cart quantity rules:", error);
      })
      .finally(() => {
        refreshInProgress = false;
      });
  }

  document.addEventListener(
    "change",
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.name.indexOf("updates[") !== 0) return;

      const item = input.closest(".cart-item[data-variant-id]");
      if (!item) return;

      const productType = knownVariants.get(String(item.dataset.variantId || ""));
      const rule = QUANTITY_RULES[productType];
      if (!rule) return;

      const current = parseInt(input.value, 10);
      const next = normalizeQuantity(current, rule, true);
      if (Number.isNaN(current) || next === current) return;

      input.value = String(next);
      setInputRule(input, rule);
      updateButtonStates(input);
    },
    true
  );

  document.addEventListener("cart:change", (event) => {
    if (event.detail && event.detail.cart) {
      hydrateFromCart(event.detail.cart);
    } else {
      refreshCartTypes();
    }
  });

  /* ---------- DOM REFRESHES ---------- */

  function scheduleApply() {
    if (applyQueued) return;
    applyQueued = true;

    window.requestAnimationFrame(() => {
      applyQueued = false;
      applyProductRule();
      applyCartRules();
    });
  }

  function start() {
    loadProductRule();
    refreshCartTypes();

    if (window.MutationObserver) {
      new MutationObserver(scheduleApply).observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
